// pages/seller/AddProduct.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import VariantForm from "../../components/Seller/VariantForm";
import { VariantFormData, Category } from "../../types/seller.type";
import { createProduct, fetchCategories } from "../../API/axios";

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const [variants, setVariants] = useState<VariantFormData[]>([
    {
      title: "",
      unit: "",
      value: 0,
      price: 0,
      stock: 0,
    },
  ]);

  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  // Fetch categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setCategoriesLoading(true);
        const data = await fetchCategories();

        // Ensure data is always an array
        let categoriesArray: Category[] = [];

        if (Array.isArray(data)) {
          categoriesArray = data;
        } else if (data && typeof data === "object") {
          // Handle case where API returns an object with categories property
          if (data.categories && Array.isArray(data.categories)) {
            categoriesArray = data.categories;
          } else if (data.data && Array.isArray(data.data)) {
            categoriesArray = data.data;
          } else if (data.items && Array.isArray(data.items)) {
            categoriesArray = data.items;
          } else {
            // Log the unexpected structure for debugging
            console.error("Unexpected categories data structure:", data);
            categoriesArray = [];
          }
        } else {
          categoriesArray = [];
        }

        setCategories(categoriesArray);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        alert("Failed to load categories. Please refresh the page.");
        setCategories([]); // Set empty array on error
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      const validFiles = files.filter((file) => file.size <= maxSize);

      if (validFiles.length !== files.length) {
        alert("Some images exceed the 5MB size limit and were not added.");
      }

      setImages((prev) => [...prev, ...validFiles]);

      // Create previews
      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews((prev) => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVariantUpdate = (
    index: number,
    field: keyof VariantFormData,
    value: string | number,
  ) => {
    setVariants((prev) =>
      prev.map((variant, i) =>
        i === index ? { ...variant, [field]: value } : variant,
      ),
    );
  };

  const addVariant = () => {
    setVariants((prev) => [
      ...prev,
      {
        title: "",
        unit: "",
        value: 0,
        price: 0,
        stock: 0,
      },
    ]);
  };

  const removeVariant = (index: number) => {
    if (variants.length > 1) {
      setVariants((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      alert("Please enter a product name");
      return false;
    }

    if (!formData.description.trim()) {
      alert("Please enter a product description");
      return false;
    }

    if (!selectedCategory) {
      alert("Please select a category");
      return false;
    }

    if (images.length === 0) {
      alert("Please add at least one product image");
      return false;
    }

    for (const v of variants) {
      if (!v.title?.trim()) {
        alert("Please fill in all variant titles");
        return false;
      }
      if (!v.unit?.trim()) {
        alert("Please fill in all variant units");
        return false;
      }
      if (!v.value || v.value <= 0) {
        alert("Please enter valid values for all variants");
        return false;
      }
      if (!v.price || v.price <= 0) {
        alert("Please enter valid prices for all variants");
        return false;
      }

      if (v.stock === undefined || v.stock < 0) {
        alert("Please enter valid stock quantities for all variants");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Prepare the data for the API call
      const productData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        categoryId: selectedCategory?.id,
        variants: variants.map((v) => ({
          title: v.title.trim(),
          unit: v.unit.trim(),
          value: Number(v.value),
          price: Number(v.price),
          stock: Number(v.stock),
        })),
        images: images,
      };

      // Validate again before sending
      if (!productData.name) {
        throw new Error("Product name is required");
      }
      if (!productData.description) {
        throw new Error("Product description is required");
      }
      if (!productData.categoryId) {
        throw new Error("Category is required");
      }

      const result = await createProduct(productData);

      alert("Product created successfully!");
      navigate("/seller/products");
    } catch (error: any) {
      console.error("Failed to create product:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create product. Please try again.";

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-22">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Add New Product</h1>
          <p className="mt-2 text-sm text-gray-600">
            Fill in the details below to add a new product to your catalog.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Basic Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                  required
                  maxLength={100}
                  placeholder="Enter product name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                  required
                  maxLength={500}
                  placeholder="Enter product description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={selectedCategory?.id || ""}
                  onChange={(e) => {
                    const category = categories.find(
                      (c) => c.id === e.target.value,
                    );
                    setSelectedCategory(category || null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-white"
                  required
                  disabled={categoriesLoading}
                >
                  <option value="" disabled>
                    {categoriesLoading
                      ? "Loading categories..."
                      : "Select a category"}
                  </option>
                  {Array.isArray(categories) &&
                    categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                </select>
                {(!Array.isArray(categories) || categories.length === 0) &&
                  !categoriesLoading && (
                    <p className="mt-1 text-sm text-red-500">
                      No categories found. Please contact support.
                    </p>
                  )}
              </div>
            </div>
          </div>

          {/* Product Images */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Product Images *
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.isArray(imagePreviews) &&
                imagePreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove image"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}

              <label className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-green-500 h-32 transition-colors">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                <span className="mt-1 text-xs text-gray-500">Add Image</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Supported formats: JPEG, PNG, JPG. Max size: 5MB per image.
            </p>
          </div>

          {/* Variants */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Product Variants *
              </h2>
              <button
                type="button"
                onClick={addVariant}
                className="px-3 py-1 text-sm text-green-600 border border-green-600 rounded-md hover:bg-green-50 transition-colors"
              >
                + Add Variant
              </button>
            </div>

            <div className="space-y-4">
              {Array.isArray(variants) &&
                variants.map((variant, index) => (
                  <VariantForm
                    key={index}
                    variant={variant}
                    index={index}
                    onUpdate={handleVariantUpdate}
                    onRemove={removeVariant}
                    showRemove={variants.length > 1}
                  />
                ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate("/seller/products")}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || categoriesLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[120px]"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                "Create Product"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProduct;
